import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { styled } from '@mui/system';
import Fullscreen from 'assets/icons/fluent_page-fit-24-regular.svg?react';
import FullscreenOff from 'assets/icons/fluent_page-off-fit-24-regular.svg?react';
import { getSimulator } from 'pages/Simulator/catalog';
import professionCases from 'helpers/professionCases';

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: '15px 35px',
  paddingBottom: 20,
  marginBottom: 25,
  borderRadius: 15,
  backgroundColor: theme.palette.grey[100],
  position: 'relative',
  // Desktop reserves room on the right for the (smaller) absolute slides button.
  paddingRight: 190,
  [theme.breakpoints.down('md')]: {
    // Tablet: reserve room on the right for the absolute top-right button.
    padding: 25,
    paddingRight: 250
  },
  [theme.breakpoints.down('sm')]: {
    backgroundColor: theme.palette.backgroundLight.main,
    padding: 0
  }
}));

const BoldText = styled(Typography)(() => ({
  fontWeight: 'bold',
  marginBottom: 15
}));

const ButtonWrapper = styled('div')(() => ({}));

const ButtonLinks = styled(Button)(({ slides, active, theme }) => ({
  color: active === 'true' ? theme.palette.primary.main : theme.palette.black.main,
  backgroundColor: active === 'true' ? theme.palette.white.main : 'transparent',
  fontSize: 14,
  padding: 0,
  display: slides === 'true' ? 'inline-block' : 'flex',
  textAlign: slides === 'true' ? 'left' : 'center',
  position: 'relative',
  '&:hover': {
    color: active === 'true' ? theme.palette.primary.main : theme.palette.black.main,
    backgroundColor: 'transparent'
  },
  '&:before': {
    content: '""',
    width: 3,
    height: 3,
    borderRadius: '50%',
    backgroundColor: active === 'true' ? theme.palette.primary.main : theme.palette.black.main,
    display: 'inline-block',
    marginRight: 15,
    position: slides === 'true' ? 'absolute' : 'relative',
    left: slides === 'true' ? -11 : 0,
    top: slides === 'true' ? 10 : 0
  }
}));

// Slide-mode sidebar link. Wide active-state pill with a left accent bar so
// the user can scan the rail and know exactly which slide is current — the
// previous form only flipped text colour and was visually indistinguishable
// from hover.
const SlidesButtonLinks = styled(Button)(({ active, theme }) => ({
  color: active === 'true' ? theme.palette.primary.main : theme.palette.grey[400],
  backgroundColor: active === 'true' ? 'rgba(121, 201, 18, 0.10)' : 'transparent',
  fontSize: 13,
  lineHeight: 1.3,
  fontWeight: active === 'true' ? 600 : 500,
  marginBottom: 2,
  padding: '8px 10px 8px 14px',
  display: 'block',
  width: '100%',
  textAlign: 'left',
  borderRadius: 8,
  position: 'relative',
  transition: 'background-color 160ms ease, color 160ms ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 4,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 2,
    backgroundColor: active === 'true' ? theme.palette.primary.main : 'transparent',
    transition: 'background-color 160ms ease'
  },
  '&:hover': {
    color: active === 'true' ? theme.palette.primary.main : theme.palette.grey[500],
    backgroundColor: active === 'true' ? 'rgba(121, 201, 18, 0.14)' : theme.palette.grey[100]
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 1
  }
}));

const Border = styled('span')(({ active, slides, theme }) => ({
  // Page-mode entries get an underline border for visual structure. In slide
  // mode the row already has a pill background + accent bar (SlidesButtonLinks),
  // so the inline border would be redundant noise — strip it there.
  borderBottom:
    slides === 'true'
      ? 'none'
      : active === 'true'
        ? `1px solid ${theme.palette.primary.main}`
        : `1px solid ${theme.palette.black.main}`,
  lineHeight: slides === 'true' ? 1.3 : '19px',
  // Several entries start with the lowercase profession name (e.g. "translator
  // salary", "translator skills") because the {{name}} placeholder is filled
  // from a lowercase declension. Capitalise just the first letter — not
  // `text-transform: capitalize`, which would also uppercase later words
  // ("Translator Skills") and break compound items like "AI impact on…".
  display: 'inline-block',
  '&::first-letter': {
    textTransform: 'uppercase'
  },
  [theme.breakpoints.down('sm')]: {
    textDecoration: slides === 'true' ? 'none' : 'underline',
    border: 'none',
    textAlign: 'left'
  }
}));

const LinksWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 15
}));

const AbsoluteFlex = styled('div')(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  justifyContent: 'flex-end',
  // Tablet: keep the button absolute, top-right — on the title's row.
  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start'
  },
  // Phone: flow inline BELOW the navigation list (rendered last via order).
  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    top: 'auto',
    height: 'auto',
    order: 1
  }
}));

const Label = styled(Button)(({ slides, theme }) => ({
  // Desktop: compact stacked button (text over icon), top-right of the list.
  fontSize: 14,
  lineHeight: '18px',
  fontWeight: 600,
  color: theme.palette.black.main,
  backgroundColor: theme.palette.secondary.main,
  padding: '6px 10px',
  textAlign: 'center',
  borderRadius: 12,
  display: 'flex',
  alignSelf: 'center',
  flex: 1,
  flexDirection: 'column',
  maxWidth: 150,
  marginRight: slides === 'true' ? 0 : 30,
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark
  },
  '& span': {
    paddingTop: 6,
    lineHeight: 'normal'
  },
  '& svg': {
    width: 22,
    height: 22
  },
  // Tablet + phone: single-line pill with the icon on the right (like the green
  // "Додати пораду" / share buttons).
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    flex: 'unset',
    width: 'auto',
    maxWidth: 'unset',
    marginRight: 25,
    marginTop: 18,
    marginBottom: 15,
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: 15,
    gap: 8,
    '& span': {
      paddingTop: 0
    },
    '& svg': {
      width: 20,
      height: 20
    }
  },
  // Phone: full-width button below the list (matches the "Додати пораду" CTA).
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    justifyContent: 'center',
    marginTop: 15,
    marginBottom: 0,
    marginRight: 0
  }
}));

const LabelFromSlides = styled(Label)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginRight: 0
  }
}));

const SlidesWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
  justifyContent: 'space-between',
  height: '100%'
}));

const SlidesLinksWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: 15,
    flex: 'unset',
    overflowY: 'visible'
  }
}));

const ToggleView = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginTop: 10,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: -10,
    right: -10,
    height: 48,
    background:
      'linear-gradient(to top, rgba(245, 245, 245, 0.95) 0%, rgba(245, 245, 245, 0) 100%)',
    pointerEvents: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: 0,
    marginBottom: 60
  },
  '& button': {
    padding: 15,
    width: '100%',
    maxWidth: 'unset',
    fontSize: 14,
    [theme.breakpoints.down('sm')]: {
      width: 160,
      maxWidth: 160,
      fontWeight: 400
    },
    '& span': {
      padding: 0,
      display: 'inline-block',
      marginBottom: 10
    }
  }
}));

const PageNavigation = ({
  handleClick,
  handleChangeView,
  professionInfo: info,
  activeStep,
  slides,
  handleCloseMenu,
  menuOpened,
  availableSteps
}) => {
  const t = useTranslate('ProfessionNavigation');

  const [items] = React.useState(() => {
    const previewAiRisk =
      typeof window !== 'undefined' && /[?&]previewAiRisk=1/.test(window.location.search);
    const showAiRisk = info?.aiRisk || previewAiRisk;

    const longText = (key, declension) => t(key, professionCases(info, declension));

    if (slides) {
      // Slide-mode list. IDs MUST match `slideNames` in
      // `pages/Profession/layout/Slides.jsx` so the active highlight tracks
      // the URL/step correctly. Order matches `SLIDE_ORDER` in that file
      // (AI risk sits between Description and Skills).
      return [
        { text: t('shortOverview'), id: 0 },
        { text: t('shortWhoIs'), id: 1 },
        ...(showAiRisk ? [{ text: t('shortAiRisk'), id: 12 }] : []),
        { text: t('shortSkills'), id: 2 },
        { text: t('shortSchedule'), id: 3 },
        { text: t('shortDuties'), id: 4 },
        { text: t('shortProsAndCons'), id: 5 },
        { text: t('shortReviews'), id: 6 },
        { text: t('shortHowToBecome'), id: 7 },
        { text: t('shortEducation'), id: 8 },
        { text: t('shortVacancies'), id: 9 },
        { text: t('shortFaq'), id: 10 },
        { text: t('shortSimilar'), id: 11 }
      ];
    }

    // Page-mode list — long labels, anchor-based IDs preserved for the
    // scroll-to-section behaviour on the standard profession page.
    const baseItems = [
      { text: longText('salary', 'genitive'), id: 0 },
      { text: longText('whoIs', 'nominative'), id: 1 },
      ...(showAiRisk ? [{ text: longText('aiRisk', 'genitive'), id: '5_ai' }] : []),
      ...(getSimulator(info?._id)
        ? [{ text: longText('simulator', 'genitive'), id: '5_sim' }]
        : []),
      { text: longText('skills', 'genitive'), id: 2 },
      { text: longText('scheduleAndConditions', 'genitive'), id: 2 },
      { text: longText('duties', 'nominative'), id: 3 },
      { text: longText('prosAndCons', 'genitive'), id: 5 }
    ];

    return baseItems.concat([
      { text: longText('tips', 'genitive'), id: 6 },
      { text: longText('howToBecome', 'instrumental'), id: 7 },
      { text: longText('vacancies', 'genitive'), id: 9 },
      { text: longText('faq', 'genitive'), id: 10 }
    ]);
  });
  const isMobile = useSelector((state) => state.app.isMobile);

  // Filter items based on available steps (for slides view)
  const filteredItems =
    slides && availableSteps ? items.filter((item) => availableSteps.includes(item.id)) : items;

  if (slides) {
    return (
      <SlidesWrapper>
        <SlidesLinksWrapper>
          {items
            .filter(({ id }) => !availableSteps || availableSteps.includes(id))
            .filter(({ id }) => {
              if (menuOpened || !isMobile) {
                return true;
              }
              return activeStep === id;
            })
            .map(({ text, id }) => {
              const isActive = activeStep === id;
              return (
                <ButtonWrapper key={text}>
                  <SlidesButtonLinks
                    slides="true"
                    onClick={() => {
                      handleClick(id);
                      handleCloseMenu();
                    }}
                    active={isActive ? 'true' : 'false'}
                    aria-current={isActive ? 'page' : undefined}>
                    <Border slides="true" active={isActive ? 'true' : 'false'}>
                      {text}
                    </Border>
                  </SlidesButtonLinks>
                </ButtonWrapper>
              );
            })}
        </SlidesLinksWrapper>
      </SlidesWrapper>
    );
  }

  return (
    <Wrapper>
      <AbsoluteFlex>
        <Label onClick={handleChangeView}>
          <span>{t('toSlides')}</span>
          <Fullscreen />
        </Label>
      </AbsoluteFlex>
      <BoldText>{t('title')}</BoldText>
      <LinksWrapper>
        {filteredItems.map(({ text, id }) => (
          <ButtonLinks key={text} onClick={() => handleClick(id)}>
            <Border>{text}</Border>
          </ButtonLinks>
        ))}
      </LinksWrapper>
    </Wrapper>
  );
};

export default PageNavigation;
